import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Chip,
    Stack,
    LinearProgress,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Alert,
    Backdrop,
    CircularProgress,
    Divider,
    Tooltip,
    Collapse,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Radio,
    RadioGroup,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    AddCircleOutline as AddIcon,
    Send as SendIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    InsertDriveFile as FileIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    HourglassEmpty as PendingIcon,
    // ArrowBack as BackIcon,
    Visibility as ViewIcon,
    Add as NewIcon,
    TableChart as ListIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    GpsFixed as PlacementIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import apiService from '../services/api.service';

// Access showSnackbar from the store
const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => ({
    type: 'ui/showSnackbar',
    payload: { message, severity },
});

interface TabPlacement {
    method: 'anchor' | 'fixed' | 'none';
    anchorString: string;
    anchorXOffset: string;
    anchorYOffset: string;
    anchorUnits: 'pixels' | 'inches' | 'cms' | 'mms';
    pageNumber: string;
    xPosition: string;
    yPosition: string;
}

interface Signer {
    name: string;
    email: string;
    tabPlacement: TabPlacement;
}

const defaultTabPlacement: TabPlacement = {
    method: 'none',
    anchorString: '',
    anchorXOffset: '0',
    anchorYOffset: '0',
    anchorUnits: 'pixels',
    pageNumber: '1',
    xPosition: '150',
    yPosition: '600',
};

interface EnvelopeStatus {
    envelopeId: string;
    status: string;
    sentDate: string | null;
    completedDate: string | null;
    signers: {
        name: string;
        email: string;
        routingOrder: number;
        status: string;
        signedDate: string | null;
        declineReason: string | null;
    }[];
}

const DocuSignSigning: React.FC = () => {
    const dispatch = useDispatch();

    // ─── Compose Form State ───────────────────────────────────────
    const [mainDocument, setMainDocument] = useState<File | null>(null);
    const [additionalDocs, setAdditionalDocs] = useState<File[]>([]);
    const [signers, setSigners] = useState<Signer[]>([{ name: '', email: '', tabPlacement: { ...defaultTabPlacement } }]);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');

    // ─── UI State ─────────────────────────────────────────────────
    const [sending, setSending] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [view, setView] = useState<'list' | 'compose' | 'tracking'>('list');
    const [sentLocalId, setSentLocalId] = useState<number | null>(null);
    const [envelopeStatus, setEnvelopeStatus] = useState<EnvelopeStatus | null>(null);
    const [statusLoading, setStatusLoading] = useState(false);

    // ─── List State ───────────────────────────────────────────────
    const [envelopes, setEnvelopes] = useState<any[]>([]);
    const [listLoading, setListLoading] = useState(false);

    const [expandedSigner, setExpandedSigner] = useState<number | null>(null);

    const mainFileRef = useRef<HTMLInputElement>(null);
    const additionalFileRef = useRef<HTMLInputElement>(null);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // ─── List Helpers ─────────────────────────────────────────────

    const fetchEnvelopes = useCallback(async () => {
        setListLoading(true);
        try {
            const result = await apiService.getDocuSignEnvelopes();
            if (result.success) {
                setEnvelopes(result.data || []);
            }
        } catch (err) {
            // Silent
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => {
        if (view === 'list') {
            fetchEnvelopes();
        }
    }, [view, fetchEnvelopes]);

    const openEnvelopeTracking = (localId: number) => {
        setSentLocalId(localId);
        setView('tracking');
    };

    const handleDownloadFromList = (localId: number) => {
        const url = apiService.getDocuSignDownloadUrl(localId);
        window.open(url, '_blank');
    };

    // ─── File Helpers ─────────────────────────────────────────────

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const handleMainDocSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) setMainDocument(file);
        if (mainFileRef.current) mainFileRef.current.value = '';
    };

    const handleAdditionalDocsSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            setAdditionalDocs((prev) => [...prev, ...Array.from(files)]);
        }
        if (additionalFileRef.current) additionalFileRef.current.value = '';
    };

    const removeAdditionalDoc = (index: number) => {
        setAdditionalDocs((prev) => prev.filter((_, i) => i !== index));
    };

    // ─── Signer Helpers ───────────────────────────────────────────

    const addSigner = () => setSigners((prev) => [...prev, { name: '', email: '', tabPlacement: { ...defaultTabPlacement } }]);

    const removeSigner = (index: number) => {
        if (signers.length <= 1) return;
        setSigners((prev) => prev.filter((_, i) => i !== index));
    };

    const updateSigner = (index: number, field: 'name' | 'email', value: string) => {
        setSigners((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
    };

    const updateSignerTab = (index: number, field: keyof TabPlacement, value: string) => {
        setSigners((prev) =>
            prev.map((s, i) =>
                i === index ? { ...s, tabPlacement: { ...s.tabPlacement, [field]: value } } : s
            )
        );
    };

    const moveSigner = (index: number, direction: 'up' | 'down') => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= signers.length) return;
        setSigners((prev) => {
            const copy = [...prev];
            [copy[index], copy[newIndex]] = [copy[newIndex], copy[index]];
            return copy;
        });
    };

    // ─── Validation ───────────────────────────────────────────────

    const validate = (): string | null => {
        if (!mainDocument) return 'Please select a main document';
        if (signers.some((s) => !s.name.trim())) return 'All signers must have a name';
        if (signers.some((s) => !s.email.trim())) return 'All signers must have an email';
        if (signers.some((s) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email)))
            return 'All signer emails must be valid';
        if (!emailSubject.trim()) return 'Email subject is required';
        return null;
    };

    // ─── Send Envelope ────────────────────────────────────────────

    const handleSend = async () => {
        const error = validate();
        if (error) {
            dispatch(showSnackbar(error, 'error'));
            return;
        }

        setSending(true);
        setUploadProgress(0);

        try {
            // Combine all files: main doc first, then additional
            const allFiles = [mainDocument!, ...additionalDocs];
            const signerData = signers.map((s, i) => ({
                name: s.name.trim(),
                email: s.email.trim(),
                order: i + 1,
                tabPlacement: s.tabPlacement.method !== 'none' ? s.tabPlacement : undefined,
            }));

            const result = await apiService.sendDocuSignEnvelope(
                allFiles,
                0, // main document is always index 0
                signerData,
                emailSubject.trim(),
                emailBody.trim(),
                (e: any) => {
                    if (e.total) {
                        setUploadProgress(Math.round((e.loaded * 100) / e.total));
                    }
                }
            );

            if (result.success) {
                dispatch(showSnackbar('Envelope sent successfully for signing!', 'success'));
                setSentLocalId(result.data.localId);
                setView('tracking');
            } else {
                dispatch(showSnackbar(result.message || 'Failed to send envelope', 'error'));
            }
        } catch (err: any) {
            const msg = err.response?.data?.message || err.message || 'Failed to send envelope';
            dispatch(showSnackbar(msg, 'error'));
        } finally {
            setSending(false);
            setUploadProgress(0);
        }
    };

    // ─── Status Polling ───────────────────────────────────────────

    const fetchStatus = useCallback(async () => {
        if (!sentLocalId) return;
        setStatusLoading(true);
        try {
            const result = await apiService.getDocuSignEnvelopeStatus(sentLocalId);
            if (result.success) {
                setEnvelopeStatus(result.data);
                // Stop polling if completed or declined
                if (['completed', 'declined', 'voided'].includes(result.data.status)) {
                    if (pollIntervalRef.current) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                    }
                }
            }
        } catch (err) {
            // Silent fail for polling
        } finally {
            setStatusLoading(false);
        }
    }, [sentLocalId]);

    useEffect(() => {
        if (view === 'tracking' && sentLocalId) {
            fetchStatus();
            // Poll every 30 seconds
            pollIntervalRef.current = setInterval(fetchStatus, 30000);
        }
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [view, sentLocalId, fetchStatus]);

    // ─── Reset ────────────────────────────────────────────────────

    const handleReset = () => {
        setMainDocument(null);
        setAdditionalDocs([]);
        setSigners([{ name: '', email: '', tabPlacement: { ...defaultTabPlacement } }]);
        setEmailSubject('');
        setEmailBody('');
        setSentLocalId(null);
        setEnvelopeStatus(null);
        setView('list');
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    // ─── Status Helpers ───────────────────────────────────────────

    const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'signed':
                return 'success';
            case 'sent':
            case 'delivered':
                return 'primary';
            case 'declined':
            case 'voided':
                return 'error';
            default:
                return 'default';
        }
    };

    const getSignerStepIcon = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'signed':
                return <CheckCircleIcon color="success" />;
            case 'declined':
                return <CancelIcon color="error" />;
            case 'sent':
            case 'delivered':
                return <PendingIcon color="primary" />;
            default:
                return <PendingIcon color="disabled" />;
        }
    };

    const isSignerComplete = (status: string) =>
        ['completed', 'signed'].includes(status?.toLowerCase());

    // ─── Download ─────────────────────────────────────────────────

    const handleDownload = () => {
        if (!sentLocalId) return;
        const url = apiService.getDocuSignDownloadUrl(sentLocalId);
        window.open(url, '_blank');
    };

    // ═══════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════

    if (view === 'list') {
        return (
            <Box component="section" aria-labelledby="ds-list-heading" sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={{ xs: 1.5, sm: 0 }}
                    sx={{ mb: 3 }}
                >
                    <Typography
                        component="h1"
                        id="ds-list-heading"
                        variant="h5"
                        fontWeight={600}
                        sx={{ fontSize: { xs: 18, sm: 22, md: 24 } }}
                    >
                        DocuSign Envelopes
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={listLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                            onClick={fetchEnvelopes}
                            disabled={listLoading}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<NewIcon />}
                            onClick={() => setView('compose')}
                            sx={{
                                background: 'linear-gradient(135deg, #354a5f 0%, #4a6580 100%)',
                                '&:hover': { background: 'linear-gradient(135deg, #2a3d4f 0%, #3d5670 100%)' },
                            }}
                        >
                            New Envelope
                        </Button>
                    </Stack>
                </Stack>

                {listLoading && envelopes.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : envelopes.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                            No envelopes sent yet. Click "New Envelope" to get started.
                        </Typography>
                    </Paper>
                ) : (
                    <Paper variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Signers</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Docs</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Sent Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: 120 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {envelopes.map((env: any) => (
                                    <TableRow
                                        key={env.localid}
                                        hover
                                        sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                                    >
                                        <TableCell>{env.localid}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                                {env.emailsubject || env.envelopeid || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={env.status?.toUpperCase()}
                                                size="small"
                                                color={getStatusColor(env.status || '')}
                                                sx={{ height: 22, fontSize: '0.7rem' }}
                                            />
                                        </TableCell>
                                        <TableCell>{env.signercount || '—'}</TableCell>
                                        <TableCell>{env.documentcount || '—'}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {env.sentdate
                                                    ? new Date(env.sentdate).toLocaleDateString()
                                                    : env.createddate
                                                    ? new Date(env.createddate).toLocaleDateString()
                                                    : '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0}>
                                                <Tooltip title="View Status">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openEnvelopeTracking(env.localid)}
                                                        color="primary"
                                                    >
                                                        <ViewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {env.status === 'completed' && (
                                                    <Tooltip title="Download Signed Document">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDownloadFromList(env.localid)}
                                                            color="success"
                                                        >
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </Box>
        );
    }

    if (view === 'tracking') {
        return (
            <Box component="section" aria-labelledby="ds-tracking-heading" sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 800, mx: 'auto' }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                    <Typography
                        component="h1"
                        id="ds-tracking-heading"
                        variant="h5"
                        fontWeight={600}
                        sx={{ fontSize: { xs: 18, sm: 22, md: 24 } }}
                    >
                        DocuSign Signing Status
                    </Typography>
                    <Chip
                        label={envelopeStatus?.status?.toUpperCase() || 'LOADING'}
                        color={getStatusColor(envelopeStatus?.status || '')}
                        size="small"
                    />
                </Stack>

                {/* Envelope Summary */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Envelope Details
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                        {emailSubject || envelopeStatus?.envelopeId}
                    </Typography>
                    {envelopeStatus?.sentDate && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Sent: {new Date(envelopeStatus.sentDate).toLocaleString()}
                        </Typography>
                    )}
                    {envelopeStatus?.completedDate && (
                        <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                            Completed: {new Date(envelopeStatus.completedDate).toLocaleString()}
                        </Typography>
                    )}
                </Paper>

                {/* Signers Progress */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Signing Progress
                    </Typography>
                    <Stepper orientation="vertical" activeStep={-1}>
                        {(envelopeStatus?.signers || []).map((signer, index) => (
                            <Step
                                key={index}
                                completed={isSignerComplete(signer.status)}
                                active={signer.status === 'sent' || signer.status === 'delivered'}
                            >
                                <StepLabel
                                    icon={getSignerStepIcon(signer.status)}
                                    optional={
                                        <Typography variant="caption" color="text.secondary">
                                            {signer.email}
                                        </Typography>
                                    }
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="body2" fontWeight={500}>
                                            {signer.name}
                                        </Typography>
                                        <Chip
                                            label={signer.status}
                                            size="small"
                                            color={getStatusColor(signer.status)}
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                        />
                                    </Stack>
                                </StepLabel>
                                <StepContent>
                                    {signer.signedDate && (
                                        <Typography variant="caption" color="success.main">
                                            Signed: {new Date(signer.signedDate).toLocaleString()}
                                        </Typography>
                                    )}
                                    {signer.declineReason && (
                                        <Alert severity="error" sx={{ mt: 1 }}>
                                            Declined: {signer.declineReason}
                                        </Alert>
                                    )}
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                </Paper>

                {/* Actions */}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ListIcon />}
                        onClick={handleReset}
                    >
                        Back to List
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={statusLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={fetchStatus}
                        disabled={statusLoading}
                    >
                        Refresh Status
                    </Button>
                    {envelopeStatus?.status === 'completed' && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            color="success"
                        >
                            Download Signed Document
                        </Button>
                    )}
                </Stack>
            </Box>
        );
    }

    // ─── Compose View ─────────────────────────────────────────────

    return (
        <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
            <Backdrop open={sending} sx={{ zIndex: 9999 }}>
                <Stack alignItems="center" spacing={2}>
                    <CircularProgress sx={{ color: '#fff' }} />
                    <Typography sx={{ color: '#fff' }}>Sending envelope to DocuSign...</Typography>
                </Stack>
            </Backdrop>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight={600}>
                    DocuSign - Send for Signing
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ListIcon />}
                    onClick={() => setView('list')}
                >
                    Back to List
                </Button>
            </Stack>

            {/* ── Section 1: Main Document ──────────────────────── */}
            <Paper sx={{ p: 3, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Main Document *
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    The primary document that requires signatures
                </Typography>

                <input
                    type="file"
                    ref={mainFileRef}
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    onChange={handleMainDocSelect}
                />

                {!mainDocument ? (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<UploadIcon />}
                        onClick={() => mainFileRef.current?.click()}
                    >
                        Select Main Document
                    </Button>
                ) : (
                    <Chip
                        icon={<FileIcon />}
                        label={`${mainDocument.name} (${formatFileSize(mainDocument.size)})`}
                        onDelete={() => setMainDocument(null)}
                        color="primary"
                        variant="outlined"
                    />
                )}
            </Paper>

            {/* ── Section 2: Additional Documents ──────────────── */}
            <Paper sx={{ p: 3, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Additional Documents
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Optional attachments to include in the envelope
                </Typography>

                <input
                    type="file"
                    ref={additionalFileRef}
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    multiple
                    onChange={handleAdditionalDocsSelect}
                />

                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UploadIcon />}
                    onClick={() => additionalFileRef.current?.click()}
                    sx={{ mb: additionalDocs.length > 0 ? 2 : 0 }}
                >
                    Add Documents
                </Button>

                {additionalDocs.length > 0 && (
                    <Stack spacing={1}>
                        {additionalDocs.map((doc, index) => (
                            <Chip
                                key={index}
                                icon={<FileIcon />}
                                label={`${doc.name} (${formatFileSize(doc.size)})`}
                                onDelete={() => removeAdditionalDoc(index)}
                                variant="outlined"
                                size="small"
                            />
                        ))}
                    </Stack>
                )}
            </Paper>

            {/* ── Section 3: Signers ───────────────────────────── */}
            <Paper sx={{ p: 3, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Signers (Sequential Order) *
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Signers will receive the document in the order listed below. Click the placement icon to configure where the signature appears.
                </Typography>

                {signers.map((signer, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1.5 }}>
                        {/* Signer row */}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={700} sx={{ minWidth: 24, textAlign: 'center' }}>
                                {index + 1}
                            </Typography>
                            <TextField
                                size="small"
                                placeholder="Full Name"
                                value={signer.name}
                                onChange={(e) => updateSigner(index, 'name', e.target.value)}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                size="small"
                                placeholder="email@example.com"
                                type="email"
                                value={signer.email}
                                onChange={(e) => updateSigner(index, 'email', e.target.value)}
                                sx={{ flex: 1 }}
                            />
                            <Tooltip title="Signature Placement">
                                <IconButton
                                    size="small"
                                    onClick={() => setExpandedSigner(expandedSigner === index ? null : index)}
                                    color={signer.tabPlacement.method !== 'none' ? 'primary' : 'default'}
                                >
                                    <PlacementIcon fontSize="small" />
                                    {expandedSigner === index ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Move Up">
                                <span>
                                    <IconButton size="small" disabled={index === 0} onClick={() => moveSigner(index, 'up')}>
                                        <ArrowUpIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Move Down">
                                <span>
                                    <IconButton size="small" disabled={index === signers.length - 1} onClick={() => moveSigner(index, 'down')}>
                                        <ArrowDownIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Remove">
                                <span>
                                    <IconButton size="small" disabled={signers.length <= 1} onClick={() => removeSigner(index)} color="error">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>

                        {/* Tab Placement expandable panel */}
                        <Collapse in={expandedSigner === index}>
                            <Box sx={{ mt: 1.5, ml: 4, p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                                    Signature Placement Method
                                </Typography>
                                <RadioGroup
                                    row
                                    value={signer.tabPlacement.method}
                                    onChange={(e) => updateSignerTab(index, 'method', e.target.value)}
                                    sx={{ mb: 1.5 }}
                                >
                                    <FormControlLabel value="none" control={<Radio size="small" />} label={<Typography variant="body2">Default (Page 1)</Typography>} />
                                    <FormControlLabel value="anchor" control={<Radio size="small" />} label={<Typography variant="body2">Anchor Text</Typography>} />
                                    <FormControlLabel value="fixed" control={<Radio size="small" />} label={<Typography variant="body2">Fixed Position</Typography>} />
                                </RadioGroup>

                                {signer.tabPlacement.method === 'anchor' && (
                                    <Stack spacing={1.5}>
                                        <Alert severity="info" sx={{ py: 0.25 }}>
                                            <Typography variant="caption">
                                                Enter a text string that appears in the PDF. The signature will be placed relative to where this text is found.
                                            </Typography>
                                        </Alert>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Anchor Text (text to search for in PDF)"
                                            placeholder='e.g. "Authorized Signature" or "Approved By"'
                                            value={signer.tabPlacement.anchorString}
                                            onChange={(e) => updateSignerTab(index, 'anchorString', e.target.value)}
                                            required
                                        />
                                        <Stack direction="row" spacing={1.5}>
                                            <TextField
                                                size="small"
                                                label="X Offset"
                                                type="number"
                                                value={signer.tabPlacement.anchorXOffset}
                                                onChange={(e) => updateSignerTab(index, 'anchorXOffset', e.target.value)}
                                                sx={{ width: 120 }}
                                                helperText="Right (+) / Left (-)"
                                            />
                                            <TextField
                                                size="small"
                                                label="Y Offset"
                                                type="number"
                                                value={signer.tabPlacement.anchorYOffset}
                                                onChange={(e) => updateSignerTab(index, 'anchorYOffset', e.target.value)}
                                                sx={{ width: 120 }}
                                                helperText="Down (+) / Up (-)"
                                            />
                                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                                <InputLabel>Units</InputLabel>
                                                <Select
                                                    value={signer.tabPlacement.anchorUnits}
                                                    label="Units"
                                                    onChange={(e) => updateSignerTab(index, 'anchorUnits', e.target.value)}
                                                >
                                                    <MenuItem value="pixels">Pixels</MenuItem>
                                                    <MenuItem value="inches">Inches</MenuItem>
                                                    <MenuItem value="cms">Centimeters</MenuItem>
                                                    <MenuItem value="mms">Millimeters</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </Stack>
                                )}

                                {signer.tabPlacement.method === 'fixed' && (
                                    <Stack spacing={1.5}>
                                        <Alert severity="info" sx={{ py: 0.25 }}>
                                            <Typography variant="caption">
                                                Specify exact page number and pixel coordinates for the signature.
                                            </Typography>
                                        </Alert>
                                        <Stack direction="row" spacing={1.5}>
                                            <TextField
                                                size="small"
                                                label="Page Number"
                                                type="number"
                                                value={signer.tabPlacement.pageNumber}
                                                onChange={(e) => updateSignerTab(index, 'pageNumber', e.target.value)}
                                                sx={{ width: 120 }}
                                                inputProps={{ min: 1 }}
                                            />
                                            <TextField
                                                size="small"
                                                label="X Position (px)"
                                                type="number"
                                                value={signer.tabPlacement.xPosition}
                                                onChange={(e) => updateSignerTab(index, 'xPosition', e.target.value)}
                                                sx={{ width: 140 }}
                                                helperText="From left edge"
                                            />
                                            <TextField
                                                size="small"
                                                label="Y Position (px)"
                                                type="number"
                                                value={signer.tabPlacement.yPosition}
                                                onChange={(e) => updateSignerTab(index, 'yPosition', e.target.value)}
                                                sx={{ width: 140 }}
                                                helperText="From top edge"
                                            />
                                        </Stack>
                                    </Stack>
                                )}

                                {signer.tabPlacement.method !== 'none' && (
                                    <Chip
                                        size="small"
                                        label={
                                            signer.tabPlacement.method === 'anchor'
                                                ? `Anchor: "${signer.tabPlacement.anchorString}" + (${signer.tabPlacement.anchorXOffset}, ${signer.tabPlacement.anchorYOffset}) ${signer.tabPlacement.anchorUnits}`
                                                : `Page ${signer.tabPlacement.pageNumber} at (${signer.tabPlacement.xPosition}, ${signer.tabPlacement.yPosition})`
                                        }
                                        color="primary"
                                        variant="outlined"
                                        sx={{ mt: 1.5 }}
                                    />
                                )}
                            </Box>
                        </Collapse>
                    </Paper>
                ))}

                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addSigner}
                    sx={{ mt: 1 }}
                >
                    Add Signer
                </Button>
            </Paper>

            {/* ── Section 4: Email Settings ────────────────────── */}
            <Paper sx={{ p: 3, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Email Settings *
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Customize the email sent to signers
                </Typography>

                <TextField
                    size="small"
                    fullWidth
                    label="Email Subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    sx={{ mb: 2 }}
                    required
                />
                <TextField
                    size="small"
                    fullWidth
                    label="Email Body"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    multiline
                    rows={4}
                    placeholder="Please review and sign the attached document..."
                />
            </Paper>

            {/* ── Section 5: Action Bar ────────────────────────── */}
            <Divider sx={{ mb: 2 }} />

            {sending && uploadProgress > 0 && (
                <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{ mb: 2, borderRadius: 1 }}
                />
            )}

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={handleSend}
                    disabled={sending}
                    sx={{
                        background: 'linear-gradient(135deg, #354a5f 0%, #4a6580 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #2a3d4f 0%, #3d5670 100%)' },
                    }}
                >
                    {sending ? 'Sending...' : 'Send for Signing'}
                </Button>
            </Stack>
        </Box>
    );
};

export default DocuSignSigning;
